import { isWhiteLabel } from "@/branding";
import type { Company } from "@/types";

/**
 * Whether the company still owes its plan, so the client shows it a mock dashboard behind the billing prompt. The wall is
 * client-side only: the API does not refuse the company's data. White-label portals sell no plans, so nothing waits on them.
 */
export function awaitsPayment(company: Company): boolean {
  return !isWhiteLabel() && !!company.payment_required;
}

/**
 * The company a client page must ask to pay before showing anything, or null when the page may load.
 *
 * A page about one company waits on that company; a page about none of the account's companies (an id it does not have
 * counts as none) waits only when every company of the account still owes its plan. Pages across several companies
 * leave the unpaid ones out instead (see `awaitsPayment`).
 */
export function companyAwaitingPayment(
  companies: readonly Company[],
  companyId?: number | null
): Company | null {
  const company = companies.find((candidate) => candidate.id === companyId);
  if (company) return awaitsPayment(company) ? company : null;
  const everyCompanyOwes =
    companies.length > 0 && companies.every(awaitsPayment);
  return everyCompanyOwes ? companies[0] : null;
}

/** Whether the account has a company it may use (paid, or never billed), so it can leave an unpaid company's wall. */
export function hasCompanyInUse(companies: readonly Company[]): boolean {
  return companies.some((company) => !awaitsPayment(company));
}
